/**
 * Header Menu Component
 * Handles mobile menu toggle and submenu interactions with full accessibility support
 */
const headerMenu = {
    options: {
        menuSelector: '.header__nav',
        toggleSelector: '.menu-toggle',
        // Flexible selector for new BEM toggle class
        submenuToggleSelector: '[class*="__toggle"]',
        // Flexible selector for submenu wrapper
        submenuWrapperSelector: '[class*="__submenu-wrapper"]',
        activeClass: 'is-active',
        openClass: 'is-open',
    },

    selectors: {
        menu: null,
        toggle: null,
        submenuToggles: null,
    },

    init() {
        this.selectors.menu = document.querySelector(this.options.menuSelector);
        this.selectors.toggle = document.querySelector(this.options.toggleSelector);
        this.selectors.submenuToggles = document.querySelectorAll(this.options.submenuToggleSelector);

        if (!this.selectors.menu) {
            return;
        }

        this.bindEvents();
        this.setupKeyboardNavigation();
    },

    bindEvents() {
        // Mobile menu toggle
        if (this.selectors.toggle) {
            this.selectors.toggle.addEventListener('click', (e) => {
                e.preventDefault();
                this.toggleMenu();
            });
        }

        // Submenu toggles
        this.selectors.submenuToggles.forEach((toggle) => {
            toggle.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                this.toggleSubmenu(toggle);
            });
        });

        // Close on outside click
        document.addEventListener('click', (e) => {
            if (this.selectors.menu && !this.selectors.menu.contains(e.target)) {
                this.closeAllSubmenus();
            }
        });

        // Close on Escape key
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape') {
                this.closeAllSubmenus();
            }
        });
    },

    setupKeyboardNavigation() {
        // Add keyboard support for parent links with aria-haspopup
        const parentLinks = this.selectors.menu.querySelectorAll('[aria-haspopup="true"]');

        parentLinks.forEach((link) => {
            link.addEventListener('keydown', (e) => {
                // Enter or Space opens submenu
                if (e.key === 'Enter' || e.key === ' ') {
                    const toggle = link.parentElement.querySelector(this.options.submenuToggleSelector);
                    if (toggle) {
                        e.preventDefault();
                        this.toggleSubmenu(toggle);
                    }
                }

                // Arrow down focuses first submenu item
                if (e.key === 'ArrowDown') {
                    const submenuId = link.getAttribute('aria-controls');
                    if (submenuId) {
                        const submenu = document.getElementById(submenuId);
                        if (submenu) {
                            e.preventDefault();
                            const firstLink = submenu.querySelector('a');
                            if (firstLink) {
                                firstLink.focus();
                            }
                        }
                    }
                }
            });
        });
    },

    toggleMenu() {
        const isOpen = this.selectors.menu.classList.toggle(this.options.openClass);
        this.selectors.toggle.classList.toggle(this.options.activeClass);
        this.selectors.toggle.setAttribute('aria-expanded', isOpen);
        document.body.classList.toggle('menu-open');
    },

    toggleSubmenu(toggle) {
        const parent = toggle.closest('li');
        const submenuWrapper = parent.querySelector(this.options.submenuWrapperSelector);
        const parentLink = parent.querySelector('[aria-haspopup="true"]');

        if (!submenuWrapper) {
            return;
        }

        const isOpen = parent.classList.toggle(this.options.openClass);
        submenuWrapper.classList.toggle(this.options.openClass);

        // Update ARIA states
        toggle.setAttribute('aria-expanded', isOpen);
        submenuWrapper.setAttribute('aria-hidden', !isOpen);

        if (parentLink) {
            parentLink.setAttribute('aria-expanded', isOpen);
        }

        // Update screen reader text
        const srText = toggle.querySelector('.screen-reader-text');
        if (srText) {
            srText.textContent = isOpen ? 'Collapse' : 'Expand';
        }
    },

    closeAllSubmenus() {
        this.selectors.submenuToggles.forEach((toggle) => {
            const parent = toggle.closest('li');
            const submenuWrapper = parent?.querySelector(this.options.submenuWrapperSelector);
            const parentLink = parent?.querySelector('[aria-haspopup="true"]');

            if (parent) {
                parent.classList.remove(this.options.openClass);
            }

            if (submenuWrapper) {
                submenuWrapper.classList.remove(this.options.openClass);
                submenuWrapper.setAttribute('aria-hidden', 'true');
            }

            toggle.setAttribute('aria-expanded', 'false');

            if (parentLink) {
                parentLink.setAttribute('aria-expanded', 'false');
            }
        });
    },
};

export { headerMenu };
